import { AutoCartSetupItem, AutoCartSetupItemDraft } from './autoCartSetup.types';
import { createStore } from 'cross-state';
import equals from 'fast-deep-equal/es6';

export type ScheduleSnapshot = {
    setups?: AutoCartSetupItem[];
    ready: boolean;
    scopeStoreGuid?: string;
    initialSnapshot?: number;
    resync: () => void;
};
export type ScheduleView = { setups?: AutoCartSetupItem[]; isLoading: boolean; error: boolean; initialSnapshot?: number };
export const scheduleScopeKey = (storeGuid?: string) => JSON.stringify([storeGuid ?? null]);
export type ScheduleObservation = { setups: AutoCartSetupItem[]; scopeStoreGuid?: string; initialSnapshot: number };
type CommandResult = 'accepted' | 'blocked' | 'inactive' | 'unavailable';
type CommandIntent =
    | { kind: 'immediate'; request: AutoCartSetupItemDraft; scopeStoreGuid?: string }
    | { kind: 'update'; request: AutoCartSetupItemDraft; baseline?: AutoCartSetupItem }
    | { kind: 'delete'; guid: string; storeGuid: string; baseline?: AutoCartSetupItem };
export type ScheduleCommand = { intent: CommandIntent } & (
    | { phase: 'submitting'; seen: Set<string> }
    | { phase: 'pending'; expected?: AutoCartSetupItem; recoveryAfter?: number }
    | { phase: 'failed'; error: unknown }
);
const fields = ['frameNumber', 'scheduledCartId', 'validFrom', 'validTo', 'storeGuid', 'cartSetupServiceParameters'] as const;
export const immediateKey = (store: string, frame: string) => JSON.stringify(['immediate', store, frame]);
const inScope = (snapshot: { scopeStoreGuid?: string }, storeGuid: string) =>
    snapshot.scopeStoreGuid === undefined || snapshot.scopeStoreGuid === storeGuid;
const contains = (setups: AutoCartSetupItem[], request: AutoCartSetupItemDraft, guid: string) =>
    setups.some(
        (setup) =>
            setup.storeGuid === request.storeGuid && setup.frameNumber === request.frameNumber && setup.scheduledCartSetupGuid === guid,
    );
function confirms(command: ScheduleCommand, snapshot: ScheduleObservation) {
    if (command.phase !== 'pending' && !(command.phase === 'failed' && command.intent.kind === 'delete')) return false;
    const { intent } = command;
    const expected = command.phase === 'pending' ? command.expected : undefined;
    const storeGuid = intent.kind === 'delete' ? intent.storeGuid : intent.request.storeGuid;
    if (!inScope(snapshot, storeGuid)) return false;
    if (intent.kind === 'delete') return !snapshot.setups.some((setup) => setup.scheduledCartSetupGuid === intent.guid);
    if (!expected) return false;
    if (intent.kind === 'immediate') return contains(snapshot.setups, intent.request, expected.scheduledCartSetupGuid);
    return snapshot.setups.some(
        (setup) =>
            setup.scheduledCartSetupGuid === expected.scheduledCartSetupGuid &&
            fields.every((field) => equals(setup[field] ?? null, expected[field] ?? null)),
    );
}

function updateConfirmationKey(setup: AutoCartSetupItem) {
    return JSON.stringify([
        setup.scheduledCartSetupGuid,
        ...fields.map((field) => {
            if (field !== 'cartSetupServiceParameters') return setup[field] ?? null;
            const parameters = setup.cartSetupServiceParameters;
            return parameters == null
                ? null
                : Object.entries(parameters)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, value]) => [key, typeof value, value]);
        }),
    ]);
}
function confirmationKey(intent: CommandIntent, expected?: AutoCartSetupItem) {
    if (intent.kind === 'delete') return 'absent';
    if (!expected) return undefined;
    return intent.kind === 'immediate' ? expected.scheduledCartSetupGuid : updateConfirmationKey(expected);
}
function collectEvidence(intent: CommandIntent, snapshot: ScheduleObservation, previous: Set<string>) {
    const storeGuid = intent.kind === 'delete' ? intent.storeGuid : intent.request.storeGuid;
    if (!inScope(snapshot, storeGuid)) return previous;
    const seen = new Set(previous);
    if (intent.kind === 'delete') {
        if (!snapshot.setups.some((setup) => setup.scheduledCartSetupGuid === intent.guid)) seen.add('absent');
    } else {
        for (const setup of snapshot.setups) {
            if (
                setup.storeGuid !== storeGuid ||
                (setup.frameNumber !== intent.request.frameNumber &&
                    !(intent.kind === 'update' && setup.scheduledCartSetupGuid === intent.baseline?.scheduledCartSetupGuid))
            )
                continue;
            seen.add(intent.kind === 'immediate' ? setup.scheduledCartSetupGuid : updateConfirmationKey(setup));
        }
    }
    return seen.size === previous.size ? previous : seen;
}

const commandTarget = (intent: CommandIntent) =>
    intent.kind === 'delete' ? { storeGuid: intent.storeGuid, frameNumber: intent.baseline?.frameNumber } : intent.request;

// Each principal owns its model. Async continuations only ever mutate that instance.
export function createScheduleModel() {
    const state = createStore({ commands: new Map<string, ScheduleCommand>(), snapshots: new Map<string, ScheduleView>() });
    const streams = new Map<string, { users: number; close: () => void; resync: () => void }>();
    let active = true;
    let initialSequence = 0;
    function publishSnapshot(scope: string, snapshot?: ScheduleView) {
        const snapshots = new Map(state.get().snapshots);
        if (snapshot) snapshots.set(scope, snapshot);
        else snapshots.delete(scope);
        state.set({ ...state.get(), snapshots });
    }
    function put(id: string, command?: ScheduleCommand) {
        const commands = new Map(state.get().commands);
        if (command) commands.set(id, command);
        else commands.delete(id);
        state.set({ ...state.get(), commands });
    }
    function observe(snapshot: ScheduleObservation) {
        initialSequence = Math.max(initialSequence, snapshot.initialSnapshot);
        const commands = new Map(state.get().commands);
        for (const [id, command] of commands) {
            if (confirms(command, snapshot)) commands.delete(id);
            else if (command.phase === 'submitting') {
                const seen = collectEvidence(command.intent, snapshot, command.seen);
                if (seen !== command.seen) commands.set(id, { ...command, seen });
            }
        }
        const snapshots = new Map(state.get().snapshots);
        snapshots.set(scheduleScopeKey(snapshot.scopeStoreGuid), {
            setups: snapshot.setups,
            initialSnapshot: snapshot.initialSnapshot,
            isLoading: false,
            error: false,
        });
        state.set({ commands, snapshots });
    }
    function forFrame(storeGuid: string, frameNumber: string) {
        return [...state.get().commands].find(([, command]) => {
            const target = commandTarget(command.intent);
            return command.phase !== 'failed' && target.storeGuid === storeGuid && target.frameNumber === frameNumber;
        });
    }
    async function execute(id: string, intent: CommandIntent, send: () => Promise<AutoCartSetupItem | undefined>): Promise<CommandResult> {
        if (!active) return 'inactive';
        const previous = state.get().commands.get(id);
        const target = commandTarget(intent);
        if ((previous && previous.phase !== 'failed') || (target.frameNumber && forFrame(target.storeGuid, target.frameNumber)))
            return 'blocked';
        if (intent.kind === 'immediate') {
            const snapshot = state.get().snapshots.get(scheduleScopeKey(intent.scopeStoreGuid));
            if (
                !intent.request.frameNumber ||
                !inScope(intent, intent.request.storeGuid) ||
                !snapshot?.setups ||
                snapshot.isLoading ||
                snapshot.error ||
                snapshot.setups.some(
                    (setup) => setup.storeGuid === intent.request.storeGuid && setup.frameNumber === intent.request.frameNumber,
                )
            )
                return 'unavailable';
        }
        put(id, { intent, phase: 'submitting', seen: new Set() });
        try {
            const expected = await send();
            const current = state.get().commands.get(id);
            if (current?.phase !== 'submitting') return 'blocked';
            const accepted: ScheduleCommand = { intent, phase: 'pending', expected };
            const key = confirmationKey(intent, expected);
            put(id, key !== undefined && current.seen.has(key) ? undefined : accepted);
            return active ? 'accepted' : 'inactive';
        } catch (error) {
            const current = state.get().commands.get(id);
            if (intent.kind === 'delete' && current?.phase === 'submitting' && current.seen.has('absent')) {
                put(id);
                return active ? 'accepted' : 'inactive';
            }
            const failure = (error as { data?: { error?: unknown } } | undefined)?.data?.error ?? error;
            put(id, { intent, phase: 'failed', error: failure });
            throw error;
        }
    }
    function canResolve(command: ScheduleCommand, snapshot: ScheduleSnapshot) {
        return (
            command.intent.kind === 'immediate' &&
            command.phase === 'pending' &&
            !!command.expected &&
            command.recoveryAfter !== undefined &&
            snapshot.ready &&
            snapshot.setups !== undefined &&
            inScope(snapshot, command.intent.request.storeGuid) &&
            snapshot.initialSnapshot !== undefined &&
            snapshot.initialSnapshot > command.recoveryAfter &&
            !contains(snapshot.setups, command.intent.request, command.expected.scheduledCartSetupGuid)
        );
    }
    return {
        state,
        observe,
        execute,
        canResolve,
        forFrame,
        nextInitialSnapshot: () => ++initialSequence,
        publishSnapshot,
        setActive: (value: boolean) => {
            active = value;
        },
        acquire(scope: string, connect: () => { close: () => void; resync: () => void }) {
            let stream = streams.get(scope);
            if (!stream) {
                stream = { users: 0, ...connect() };
                streams.set(scope, stream);
            }
            stream.users++;
            return () => {
                if (--stream.users === 0) {
                    stream.close();
                    streams.delete(scope);
                    publishSnapshot(scope);
                }
            };
        },
        resync: (scope: string) => streams.get(scope)?.resync(),
        acknowledge: (id: string) => {
            if (state.get().commands.get(id)?.phase !== 'submitting') put(id);
        },
        reload(id: string, snapshot: ScheduleSnapshot) {
            const command = state.get().commands.get(id);
            if (command?.phase !== 'pending') return;
            put(id, { ...command, recoveryAfter: Math.max(initialSequence, snapshot.initialSnapshot ?? 0) });
            snapshot.resync();
        },
        resolve(id: string, snapshot: ScheduleSnapshot) {
            const command = state.get().commands.get(id);
            if (command && (command.phase === 'failed' || canResolve(command, snapshot))) put(id);
        },
    };
}
export type ScheduleModel = ReturnType<typeof createScheduleModel>;
